import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemIcon,
  Stack,
  Dialog,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Schedule as ScheduleIcon,
  Devices as DevicesIcon,
  DateRange as DateRangeIcon,
  Timer as TimerIcon,
  Monitor as MonitorIcon,
  SmartDisplay as SmartDisplayIcon,
  Scale as ScaleIcon,
  InfoOutlined as InfoIcon,
  LabelOutlined as LabelIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  History as HistoryIcon,
  Description as DescriptionIcon, // Icona Testo
  Restaurant as RestaurantIcon, // Icona Ingredienti
  ConfirmationNumber as TicketIcon, // Icona Ticket
  LowPriority as PriorityIcon, // Icona Priorità
  People as QueueIcon, // Icona Coda
  Storefront as StorefrontIcon, // Icona Banchi
  ShoppingCart as ArticleIcon, // Icona Articoli
} from "@mui/icons-material";
import { format } from "date-fns";
import ReactPlayer from "react-player";
import { it } from "date-fns/locale";

// Helper per formattare i secondi
const formatPlayerTime = (secondsStr) => {
  if (!secondsStr) return null;
  const seconds = parseInt(secondsStr, 10);
  if (isNaN(seconds) || seconds <= 0) return null;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

// Helper per il tipo di file
const getFileType = (fileName, item) => {
  if (!fileName) return "unsupported";
  const extension = fileName.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) return "image";
  if (["mp4", "mov", "avi", "wmv", "webm"].includes(extension)) return "video";
  if (extension === "pdf") return "pdf";
  if (item.content_type === "TXT") return "text"; // Gestione Testo
  return "unsupported";
};

// Helper per le icone dei dispositivi
const DeviceTypeIcon = ({ type }) => {
  switch (String(type)) {
    case "1":
      return <MonitorIcon fontSize="small" />;
    case "2":
      return <SmartDisplayIcon fontSize="small" />;
    case "3":
      return <ScaleIcon fontSize="small" />;
    default:
      return <DevicesIcon fontSize="small" />;
  }
};

// Helper per ottenere il nome del tipo di dispositivo
const getDeviceTypeName = (type) => {
  switch (String(type)) {
    case "1":
      return "Monitor";
    case "2":
      return "Totem";
    case "3":
      return "Bilance";
    default:
      return "Dispositivi";
  }
};

// Helper per i giorni di riproduzione
const PlayDays = ({ days }) => {
  if (!days) return null;
  const dayChars = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  return (
    <Box display="flex" gap={0.5}>
      {dayChars.map((day, index) => (
        <Typography
          key={index}
          variant="body2"
          fontWeight={days[index] === "1" ? "bold" : "normal"}
          color={days[index] === "1" ? "text.primary" : "text.disabled"}
        >
          {day}
        </Typography>
      ))}
    </Box>
  );
};

// Logica di Scheduling (Invariata)
const mediaScheduling = (item) => {
  if (!item.play_date_start) return "Unknown"; // Safety check for Totem items without dates

  const currentDateTime = new Date();
  const currentDayOfWeek = currentDateTime.getDay();

  const playDaysArray = (item.play_days || "1111111")
    .split("")
    .map((d) => d === "1");
  const isTodayPlayable = playDaysArray[(currentDayOfWeek + 6) % 7];

  const startDate = new Date(item.play_date_start.replace(/\//g, "-"));
  const endDate = new Date(item.play_date_end.replace(/\//g, "-"));
  endDate.setHours(23, 59, 59, 999);

  const startTime = new Date(
    `1970-01-01T${item.play_time_start || "00:00:00"}`
  );
  const endTime = new Date(`1970-01-01T${item.play_time_end || "23:59:59"}`);
  const currentTime = new Date(
    `1970-01-01T${currentDateTime.toTimeString().slice(0, 8)}`
  );

  if (currentDateTime < startDate) return "Scheduled";
  else if (currentDateTime > endDate) return "Expired";
  else {
    if (isTodayPlayable && currentTime >= startTime && currentTime <= endTime)
      return "Playing";
    else if (isTodayPlayable && currentTime < startTime) return "Scheduled";
    else return "Paused";
  }
};

const getSchedulingStatusProps = (status) => {
  switch (status) {
    case "Playing":
      return {
        color: "success",
        icon: <SmartDisplayIcon />,
        label: "In Riproduzione",
      };
    case "Scheduled":
      return { color: "info", icon: <ScheduleIcon />, label: "Programmato" };
    case "Paused":
      return { color: "warning", icon: <PauseIcon />, label: "In Pausa" };
    case "Expired":
      return { color: "error", icon: <HistoryIcon />, label: "Scaduto" };
    default:
      return { color: "default", icon: <InfoIcon />, label: status };
  }
};

export default function ContentCardV2({ item, onDelete }) {
  const [devicesAnchorEl, setDevicesAnchorEl] = useState(null);
  const [totemTargetsAnchorEl, setTotemTargetsAnchorEl] = useState(null); // Nuovo popover per target totem
  const [isImageDialogOpen, setImageDialogOpen] = useState(false);
  const [isImageLoading, setImageLoading] = useState(true);
  const [textContent, setTextContent] = useState(null); // Stato per il contenuto del file testo

  const fileType = getFileType(item?.original_name, item);
  const fullUrlThumbs = item ? item.web_thumbnail_path : null;
  const fullUrlFullSize = item ? item.web_path : null;

  // Rileva se è un elemento Totem controllando se esiste
  const isTotemItem = item?.media_target === "TOTEM";
  // Normalizzazione dati totem (se appiattiti o annidati)
  const totemData = item?.json_data || item;

  useEffect(() => {
    // Se apriamo il dialog ed è un file di testo, scarichiamo il contenuto
    if (isImageDialogOpen && fileType === "text" && fullUrlFullSize) {
      setTextContent(item.content_value); // il testo é dentro web_path per semplicitá
    }
  }, [isImageDialogOpen, fileType, fullUrlFullSize]);

  if (!item) return null;

  const {
    image_state,
    original_name,
    image_desc,
    image_edited_resolution,
    image_edited_size,
    content_set,
    play_date_start,
    play_date_end,
    play_time_start,
    play_time_end,
    play_days,
    device_ids = [],
    player_time,
  } = item;

  const isScheduled = image_state === "WEB";
  const statusColor = isScheduled ? "success" : "warning";
  const schedulingStatus = mediaScheduling(item);
  const {
    color: schedulingColor,
    icon: schedulingIcon,
    label: schedulingLabel,
  } = getSchedulingStatusProps(schedulingStatus);

  const handleOpenImageDialog = () => {
    setImageDialogOpen(true);
  };
  const handleCloseImageDialog = () => setImageDialogOpen(false);

  // --- RENDERING MEDIA PREVIEW (CARD) ---
  const MediaViewer = () => {
    switch (fileType) {
      case "video":
        return (
          <Box sx={{ position: "relative", lineHeight: 0 }}>
            <Box
              component={ReactPlayer}
              src={fullUrlFullSize}
              muted={true}
              autoPlay={false} // Autoplay video in grid can be heavy
              playing={false} // Meglio false in grid view per performance
              width="100%"
              height="auto"
              style={{ maxHeight: 200, objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            {/* Overlay Play Icon se necessario, oppure l'icona video sopra */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.1)",
              }}
            >
              <SmartDisplayIcon
                sx={{ fontSize: 48, color: "rgba(255,255,255,0.8)" }}
              />
            </Box>
          </Box>
        );
      case "text":
        return (
          <Box
            sx={{
              height: 200,
              width: "100%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              p: 2,
            }}
          >
            <DescriptionIcon
              sx={{ fontSize: 60, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" align="center">
              Testo completo
            </Typography>
          </Box>
        );
      case "pdf":
        return (
          <Box
            sx={{
              height: 200,
              width: "100%",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              PDF Preview
            </Typography>
          </Box>
        );
      case "image":
      default:
        return (
          <Box
            sx={{
              position: "relative",
              lineHeight: 0,
              height: 200,
              bgcolor: "grey.100",
            }}
          >
            <Box
              component="img"
              src={fullUrlThumbs}
              alt={image_desc || original_name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover", // Cover riempie meglio la card
              }}
            />
          </Box>
        );
    }
  };

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          border: "none",
          position: "relative",
          transition: "box-shadow 0.3s",
          "&:hover": { boxShadow: 4 },
        }}
      >
        {/* Header Media */}
        <Box
          sx={{ position: "relative", cursor: "pointer" }}
          onClick={handleOpenImageDialog}
        >
          <MediaViewer />

          {/* Pulsante Fullscreen */}
          <Tooltip title="Visualizza contenuto">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenImageDialog();
              }}
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
              }}
            >
              <FullscreenIcon />
            </IconButton>
          </Tooltip>

          {/* Chips Stato */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <Chip
              label={image_state}
              color={statusColor}
              size="small"
              sx={{ fontWeight: "bold", boxShadow: 1 }}
            />
            {isScheduled && (
              <Chip
                icon={schedulingIcon}
                label={schedulingLabel}
                color={schedulingColor}
                size="small"
                sx={{ fontWeight: "bold", boxShadow: 1 }}
              />
            )}
          </Stack>

          {/* Durata Video */}
          {fileType === "video" && formatPlayerTime(player_time) && (
            <Chip
              icon={<TimerIcon />}
              label={formatPlayerTime(player_time)}
              size="small"
              sx={{
                position: "absolute",
                bottom: 8,
                left: 8,
                zIndex: 1,
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "white",
              }}
            />
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2, pb: 1 }}>
          <Tooltip title={image_desc || original_name}>
            <Typography
              variant="h6"
              component="div"
              noWrap
              sx={{ mb: 2, fontWeight: 600 }}
            >
              {isTotemItem && item.content_type === "TXT" ? item.content_value.slice(0, 15)+"..." : original_name}
            </Typography>
          </Tooltip>

          <List dense disablePadding>
            <>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <DateRangeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Periodo"
                  secondary={
                    play_date_start === play_date_end
                      ? format(
                          new Date(play_date_start.replace(/\//g, "-")),
                          "d MMM yyyy",
                          { locale: it }
                        )
                      : `${format(
                          new Date(play_date_start.replace(/\//g, "-")),
                          "d MMM",
                          { locale: it }
                        )} - ${format(
                          new Date(play_date_end.replace(/\//g, "-")),
                          "d MMM",
                          { locale: it }
                        )}`
                  }
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ScheduleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Giorni"
                  secondary={
                    play_days === "1111111" ? (
                      "Tutti i giorni"
                    ) : (
                      <PlayDays days={play_days} />
                    )
                  }
                />
              </ListItem>
            </>
            {isTotemItem ? (
              // --- VISTA TOTEM ---
              <>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {totemData.print_function === 1 ? (
                      <TicketIcon color="primary" />
                    ) : (
                      <RestaurantIcon color="secondary" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Trigger Stampa"
                    secondary={
                      totemData.print_function === 1
                        ? "Prenotazione Ticket"
                        : "Stampa Ingredienti"
                    }
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <PriorityIcon fontSize="small" color="action" />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary="Regole Coda"
                    secondary={
                      <span
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          Priorità:{" "}
                          <b>{totemData.content_priority || 5}</b>
                        </span>
                        <span>•</span>
                        <span>
                          Soglia: &gt;
                          <b>{totemData.print_cond_queue_min || 0}</b>
                        </span>
                      </span>
                    }
                  />
                </ListItem>
                <ListItem
                  button
                  disableGutters
                  onClick={(e) => setTotemTargetsAnchorEl(e.currentTarget)}
                  sx={{
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                    px: 0.5,
                    ml: -0.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <StorefrontIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Target (Banchi/Articoli)"
                    secondary="Clicca per dettagli"
                  />
                </ListItem>
              </>
            ) : null}
          </List>

          <Divider sx={{ my: 1.5 }} />

          <List dense disablePadding>
            <ListSubheader
              sx={{ bgcolor: "transparent", lineHeight: "24px", pl: 0, mb: 1 }}
            >
              Dettagli Tecnici
            </ListSubheader>

            {/* Sezione Dispositivi (Comune a entrambi, o specifico monitor) */}
            <ListItem
              button
              disableGutters
              onClick={(e) => setDevicesAnchorEl(e.currentTarget)}
              sx={{
                borderRadius: 1,
                "&:hover": { bgcolor: "action.hover" },
                px: 0.5,
                ml: -0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <DevicesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Dispositivi"
                secondary={`${device_ids.length} assegnati`}
              />
            </ListItem>

            <ListItem disableGutters sx={{ px: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LabelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Content Set" secondary={content_set} />
            </ListItem>
          </List>
        </CardContent>

        {/* --- POPOVERS --- */}

        {/* 1. Popover Dispositivi (Standard) */}
        <Popover
          open={Boolean(devicesAnchorEl)}
          anchorEl={devicesAnchorEl}
          onClose={() => setDevicesAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <List
            dense
            sx={{
              minWidth: 250,
              maxWidth: 300,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            <ListSubheader>Dispositivi Assegnati</ListSubheader>
            {device_ids.length > 0 ? (
              device_ids.map((dev, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <DeviceTypeIcon type={dev.single_dev_type} />
                  </ListItemIcon>
                  <ListItemText
                    primary={dev.single_dev_desc || dev.single_dev_id}
                    secondary={
                      dev.single_dev_id === "ALL"
                        ? "Tutti i dispositivi"
                        : `ID: ${dev.single_dev_id}`
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText secondary="Nessun dispositivo specifico." />
              </ListItem>
            )}
          </List>
        </Popover>

        {/* 2. Popover Target Totem (Nuovo) */}
        <Popover
          open={Boolean(totemTargetsAnchorEl)}
          anchorEl={totemTargetsAnchorEl}
          onClose={() => setTotemTargetsAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <List
            dense
            sx={{
              minWidth: 250,
              maxWidth: 300,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            <ListSubheader
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <StorefrontIcon fontSize="small" /> Banchi Abilitati
            </ListSubheader>
            {/* Gestione array banchi: assume totemData.counters sia un array o stringa csv */}
            {totemData.print_cond_banco &&
            totemData.print_cond_banco.length > 0 ? (
              (Array.isArray(totemData.print_cond_banco)
                ? totemData.print_cond_banco
                : totemData.print_cond_banco.split(",")
              ).map((c, i) => (
                <ListItem key={"c" + i}>
                  <ListItemText primary={c} />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText secondary="Nessun banco trigger" />
              </ListItem>
            )}

            <Divider />

            <ListSubheader
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ArticleIcon fontSize="small" /> Articoli Trigger
            </ListSubheader>
            {totemData.print_cond_arts &&
            totemData.print_cond_arts.length > 0 ? (
              (Array.isArray(totemData.print_cond_arts)
                ? totemData.print_cond_arts
                : totemData.print_cond_arts.split(",")
              ).map((a, i) => (
                <ListItem key={"a" + i}>
                  <ListItemText primary={a} />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText secondary="Nessun articolo trigger" />
              </ListItem>
            )}
          </List>
        </Popover>
      </Card>

      {/* --- DIALOG FULLSCREEN --- */}
      <Dialog
        open={isImageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="xl"
        PaperProps={{
          sx: {
            backgroundColor:
              fileType === "text" ? "background.paper" : "rgba(0, 0, 0, 0.85)", // Sfondo chiaro per testo, scuro per media
            boxShadow: "none",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "50vw",
            minHeight: "50vh",
          },
        }}
      >
        <Tooltip title="Chiudi">
          <IconButton
            onClick={handleCloseImageDialog}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: fileType === "text" ? "text.primary" : "white", // Colore X adattivo
              backgroundColor:
                fileType === "text" ? "rgba(0,0,0,0.1)" : "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor:
                  fileType === "text"
                    ? "rgba(0,0,0,0.2)"
                    : "rgba(0, 0, 0, 0.8)",
              },
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>

        {(() => {
          switch (fileType) {
            case "video":
              return (
                <Box
                  component={ReactPlayer}
                  src={fullUrlFullSize}
                  controls={true}
                  playing={true}
                  width="auto"
                  height="auto"
                  style={{ maxWidth: "90vw", maxHeight: "90vh" }}
                />
              );
            case "text":
              return (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    width: "100%",
                    maxWidth: 800,
                    maxHeight: "80vh",
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <DescriptionIcon color="primary" /> Contenuto Ticket
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body1"
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      bgcolor: "grey.50",
                      p: 2,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {textContent || "Nessun testo disponibile."}
                  </Typography>
                </Paper>
              );
            case "image":
              return (
                <Box
                  component="img"
                  src={fullUrlFullSize}
                  sx={{
                    maxWidth: "95vw",
                    maxHeight: "90vh",
                    objectFit: "contain",
                  }}
                />
              );
            default:
              return (
                <Typography sx={{ color: "white" }}>
                  Anteprima non disponibile
                </Typography>
              );
          }
        })()}
      </Dialog>
    </>
  );
}
